import React from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import { 
  LayoutDashboard, 
  Send, 
  Inbox, 
  CheckSquare, 
  Search, 
  Users, 
  Building2, 
  FileText, 
  LogOut,
  ChevronRight,
  ShieldCheck,
  Settings,
  FileCheck,
  FolderOpen,
  Layers,
  Archive,
  UserCheck,
  BookUser,
  BarChart3,
  X
} from 'lucide-react';

const Sidebar = ({ isOpen = false, onClose }) => {
  const { user, logout } = useAuth();
  const { settings, loading: settingsLoading } = useSettings();
  
  // Check if e-File module is enabled (handle string or number '1'/'0', true/false)
  const isEfileEnabled = settings?.efile_enabled == 1 || settings?.efile_enabled === 'true' || settings?.efile_enabled === true;
  const role = user?.role?.toLowerCase() || '';
  const email = user?.email?.toLowerCase() || '';

  const pageAccess = settings?.role_page_access;
  const normalizeRole = (v) => String(v || '').trim().toLowerCase().replace(/\s+/g, ' ');
  const resolveRoleKey = (obj, role) => {
    if (!obj || typeof obj !== 'object') return role;
    const r = normalizeRole(role);
    const keys = Object.keys(obj);
    const exact = keys.find(k => String(k).trim() === String(role || '').trim());
    if (exact) return exact;
    const ci = keys.find(k => normalizeRole(k) === r);
    return ci || role;
  };

  const roleKey = resolveRoleKey(pageAccess, user?.role);
  const allowedPagesRaw = pageAccess && typeof pageAccess === 'object' ? pageAccess[roleKey] : null;
  const allowedPages = Array.isArray(allowedPagesRaw)
    ? allowedPagesRaw
    : (pageAccess && typeof pageAccess === 'object')
      ? []
      : settingsLoading
        ? []
        : ['dashboard', 'profile', 'notifications'];
  const hasPageAccess = (pageKey) => {
    return allowedPages.includes(pageKey);
  };
  const hasAnyPageAccess = (keys) => {
    return keys.some(k => allowedPages.includes(k));
  };

  const navKeyForPath = (to) => {
    const p = String(to || '');
    if (p === '/' || p === '/dashboard') return 'dashboard';
    if (p === '/kanban') return 'efile_my_desk';
    if (p === '/files') return 'efile_workspace';
    if (p === '/record-room') return 'record_room';
    if (p === '/delegations') return 'delegations';
    if (p === '/address-book') return 'address_book';
    if (p === '/dispatch') return 'dispatch_list';
    if (p === '/diary') return 'diary_list';
    if (p === '/compliance') return 'compliance';
    if (p === '/approvals') return 'approvals';
    if (p === '/search') return 'search';
    if (p === '/reports') return 'reports';
    if (p === '/users') return 'user_management';
    if (p === '/audit') return 'audit_trail';
    if (p === '/settings') return 'system_settings';
    if (p === '/offices') return 'office_management';
    return null;
  };

  const links = [
    { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
  ];

  if (isEfileEnabled) {
    links.push(
      { to: '/kanban', icon: FolderOpen, label: 'e-File: My Desk' },
      { to: '/files', icon: Layers, label: 'e-File: Workspace' },
      { to: '/record-room', icon: Archive, label: 'Record Room (Archives)' },
      { to: '/delegations', icon: UserCheck, label: 'Delegations' }
    );
  }

  links.push(
    { to: '/address-book', icon: BookUser, label: 'Address Book' },
    { to: '/dispatch', icon: Send, label: 'Dispatch Register' },
    { to: '/diary', icon: Inbox, label: 'Diary Register' },
    { to: '/compliance', icon: CheckSquare, label: 'Compliance' },
    { to: '/search', icon: Search, label: 'Search' },
  );

  links.splice(links.findIndex(l => l.to === '/search'), 0, { to: '/reports', icon: BarChart3, label: 'Reports' });

  if (isEfileEnabled) {
    links.splice(links.findIndex(l => l.to === '/compliance'), 0, { to: '/approvals', icon: FileCheck, label: 'Pending Approvals' });
  }

  // Debug role to console
  React.useEffect(() => {
    // Console logs removed for production
  }, [user]);

  links.push({ to: '/users', icon: Users, label: 'User Management' });
  links.push({ to: '/audit', icon: FileText, label: 'Audit Trail' });
  links.push({ to: '/settings', icon: Settings, label: 'System Settings' });
  links.push({ to: '/offices', icon: Building2, label: 'Office Management' });

  const filteredLinks = links.filter(l => {
    const key = navKeyForPath(l.to);
    if (!key) return true;
    if (key === 'diary_list') {
      return hasAnyPageAccess(['diary_list', 'diary_create', 'diary_edit', 'diary_preview']);
    }
    if (key === 'dispatch_list') {
      return hasAnyPageAccess(['dispatch_list', 'dispatch_create', 'dispatch_edit', 'dispatch_preview', 'dispatch_print']);
    }
    return hasPageAccess(key);
  });

  const content = (
    <div className="z-30 flex h-full w-72 flex-col border-r border-indigo-100/80 bg-[linear-gradient(180deg,rgba(15,23,42,0.97),rgba(30,41,59,0.96),rgba(15,23,42,0.97))] text-white shadow-[0_30px_90px_rgba(30,41,59,0.26)] backdrop-blur-xl transition-all duration-300">
      <div className="border-b border-white/10 bg-white/5 p-4 backdrop-blur-xl">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 via-violet-500 to-cyan-400 shadow-[0_12px_30px_rgba(99,102,241,0.45)] ring-1 ring-white/20">
            <ShieldCheck className="h-6 w-6 text-white" />
          </div>
          <div className="min-w-0">
            <h1 className="text-lg font-bold tracking-tight text-white">
              DAK <span className="font-light text-indigo-200">System</span>
            </h1>
            <p className="mt-1 text-[9px] font-bold uppercase tracking-[0.22em] text-slate-300/80">Compliance Portal</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ml-auto inline-flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-slate-200 hover:bg-white/10 2xl:hidden"
            aria-label="Close menu"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        {user?.officeName && (
          <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-3 shadow-inner shadow-white/5">
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-300">Office</p>
            <p className="mt-1 truncate text-sm font-semibold text-slate-100" title={user.officeName}>{user.officeName}</p>
            <p className="mt-1 text-xs font-mono text-slate-300/80">{user.officeCode}</p>
          </div>
        )}
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
        <div className="mb-2 flex items-center justify-between border-b border-white/10 px-2 pb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-300/80">
          <span>Main Menu</span>
          <span className="rounded-full bg-emerald-400/10 px-1.5 py-0.5 text-[9px] font-bold text-emerald-300">v1.6</span>
        </div>
        {filteredLinks.map((link) => (
          <NavLink
            key={link.to}
            to={link.to}
            className={({ isActive }) =>
              `group relative flex items-center rounded-2xl px-3 py-2.5 text-sm font-medium transition-all duration-200 ease-in-out ${
                isActive
                  ? 'bg-gradient-to-r from-indigo-500/30 to-violet-500/15 text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.12),0_10px_25px_rgba(99,102,241,0.18)]'
                  : 'text-slate-200/80 hover:bg-white/8 hover:text-white'
              }`
            }
          >
            {({ isActive }) => (
              <>
                {isActive && <div className="absolute inset-y-1 left-0 w-1 rounded-r-full bg-gradient-to-b from-indigo-300 to-cyan-300" />}
                <link.icon className={`mr-3 h-5 w-5 transition-colors ${isActive ? 'text-indigo-200' : 'text-slate-300/80 group-hover:text-white'}`} />
                <span className="flex-1 tracking-wide">{link.label}</span>
                {isActive && <ChevronRight className="h-4 w-4 text-indigo-200" />}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-white/10 bg-white/5 p-4 backdrop-blur-xl">
        <NavLink
          to="/profile"
          className="group flex items-center gap-3 rounded-2xl p-2.5 transition-colors hover:bg-white/8"
        >
          <div className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-full border border-indigo-300/40 bg-gradient-to-br from-indigo-400/30 to-cyan-400/20 ring-1 ring-white/10">
            {user?.profilePicture ? (
              <img
                src={user.profilePicture.startsWith('http') ? user.profilePicture : `/api/users/${user.id}/profile-picture`}
                alt={user.name}
                className="h-full w-full object-cover"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
            ) : null}
            <span className="text-sm font-bold text-indigo-100">{user?.name?.charAt(0) || 'U'}</span>
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-slate-100 group-hover:text-white">{user?.name || 'User'}</p>
            <p className="truncate text-[11px] text-slate-300/80">{user?.designation || user?.role || 'Officer'}</p>
          </div>
          <ChevronRight className="h-4 w-4 text-slate-300/80 group-hover:text-white" />
        </NavLink>

        <button
          onClick={logout}
          className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-xs font-medium text-slate-200 transition-colors hover:border-red-400/40 hover:bg-red-500/10 hover:text-red-200"
        >
          <LogOut className="h-3.5 w-3.5" />
          <span>Sign Out</span>
        </button>
      </div>
    </div>
  );

  return (
    <>
      <div className="hidden 2xl:flex h-full">{content}</div>
      {isOpen && (
        <div className="2xl:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/40" onClick={onClose} />
          <div className="absolute inset-y-0 left-0">{content}</div>
        </div>
      )}
    </>
  );
};

export default Sidebar;
